/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { OnepassItem } from 'client-code/onepass';

import { DeviceModel } from '../../../core/device';
import { BrandingUiInfoKeys } from '../../../core/entities';
import { InteractiveComponents } from '../../../core/enums';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import { NdvrInteractor, ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { KeyManager, ScreenTaskControllerTypes } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { TGetState, TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TUnifiedCollectionPreview } from '../../../core/types';
import { BrandingUiUtility, ListTypes, UniqueIdentifier } from '../../../core/utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../../interactivelayer/interactiveLayer.reducers';
import { setListEntry, setListEntryFocus } from '../../list/list.actions';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../list/list.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showPCPinOverlayWithProgramRating,
} from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { collectionScreenTransition } from '../../screens/navigation/screensnavigation';
import { showGuideScreen, showPreviousScreen } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    resetOnePassListAction,
    setOnePassIsListLoadedAction,
    setOnePassListOffsetAction,
    setOnePassListValuesAction,
    setOnePassManagerIsCaretVisibleAction,
} from './onepassmanager.actions';
import {
    RESET_ONEPASS_LIST,
    SET_ONEPASS_MANAGER_IS_CARET_VISIBLE,
    SET_ONEPASS_MANAGER_IS_LIST_LOADED,
    SET_ONEPASS_MANAGER_LIST,
    SET_ONEPASS_MANAGER_OFFSET,
} from './onepassmanager.types';

interface IOnePassManager {
    isCaretVisible: boolean;
    isScreenLoaded: boolean;
    isListLoaded: boolean;
    lastSelectedPosition: number;
    offset: number;
    onePassList: Array<OnepassItem>;
    totalCount: number;
}
/* lastSelectedPosition:This is the position of last selected item that is navigated to slt screen. 
 Not updating this on each key nav like key up, down etc, it is updated only on key ok on item.
 as back naviation from slt to opm screen needs to remember the last selected item,
 while it should be reset when going from opm to settings main screen and coming back to opm */

const initialState: IOnePassManager = {
    isCaretVisible: false,
    isScreenLoaded: false,
    isListLoaded: false,
    lastSelectedPosition: 0,
    offset: 0,
    onePassList: [],
    totalCount: 0,
};

const TAG: string = 'OnePassManagerList';
const OPM_LIST_CALIBRATION = 14; // limit for 1st and last page
const OPM_LIST_CALIBRATION2 = 15; // limit for mid page

const onePassManager = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IOnePassManager>>{},
) => {
    switch (type) {
        case RESET_ONEPASS_LIST:
            return {
                ...state,
                isScreenLoaded: false,
                isListLoaded: false,
                lastSelectedPosition: payload.lastSelectedPosition,
                offset: payload.offset,
                onePassList: payload.onePassList,
                totalCount: payload.totalCount,
            };
        case SET_ONEPASS_MANAGER_IS_CARET_VISIBLE:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case SET_ONEPASS_MANAGER_LIST:
            return {
                ...state,
                isScreenLoaded: payload.isScreenLoaded,
                onePassList: payload.onePassList,
                totalCount: payload.totalCount,
            };
        case SET_ONEPASS_MANAGER_OFFSET:
            return {
                ...state,
                offset: payload.offset,
            };
        case SET_ONEPASS_MANAGER_IS_LIST_LOADED:
            return {
                ...state,
                isListLoaded: payload.isListLoaded,
            };
        default:
            return state;
    }
};

const showCollectionsScreen = (unifiedCollectionId: string) => async (dispatch: Function) => {
    dispatch(
        collectionScreenTransition({
            unifiedCollectionId,
            transitionScreen: TransitionScreenType.ONEPASS_MANAGER,
        }),
    );
};

export const updateOnePassList =
    (offset: number, highlightPosition?: number) => async (dispatch: Function) => {
        try {
            if (NetworkManager.isNetworkDisconnected()) {
                dispatch(
                    showWhisper({
                        message: NetworkManager.getNetworkDisconnectedMessage(),
                        iconType: WhisperTypes.INFO,
                    }),
                );
                dispatch(setOnePassIsListLoadedAction(true));
                return;
            }

            const networkCallId = UniqueIdentifier.getNextOnePassManagerNetworkCallId();
            const services = new ServicesClientImpl();
            const res = await services.getOnePassList({
                offset,
                count: offset === 0 ? OPM_LIST_CALIBRATION : OPM_LIST_CALIBRATION2,
                networkCallId,
            });
            if (res.networkCallId !== UniqueIdentifier.getOnePassManagerNetworkCallId()) return;

            const onePassList = res.onePassItems;

            /* Scenario: When transitioning to the series screen and deleting one pass,
              if there is only one item remaining in the list (which is the chopped item),
              then fetch the previous page itself */
            if (offset > 0 && onePassList.length === 1) {
                const newOffset =
                    offset < OPM_LIST_CALIBRATION ? 0 : offset - OPM_LIST_CALIBRATION2 + 2;
                dispatch(
                    updateOnePassList(
                        newOffset,
                        newOffset === 0 ? OPM_LIST_CALIBRATION - 2 : OPM_LIST_CALIBRATION2 - 2,
                    ),
                );
                return;
            }

            dispatch(setOnePassListValuesAction(true, onePassList, res.totalCount));
            dispatch(setOnePassListOffsetAction(offset));
            dispatch(setOnePassIsListLoadedAction(true));

            if (onePassList?.length) {
                let highlightPos = highlightPosition || 0;
                /* Scenario: When transitioning to the series screen and deleting one pass,
                  the lastSelectedPosition of the element will become invalid and out of bounds.
                  In this case, select lastSelectedPosition - 1. */
                if (highlightPos >= onePassList.length) highlightPos = onePassList.length - 1;

                dispatch(
                    setListEntry(ListTypes.ONEPASS_MANAGER, {
                        position: highlightPos,
                        limit: onePassList.length,
                        isFocused: true,
                    }),
                );
            }
        } catch (error) {
            // Incase of failure, streamers shows empty list message with no error whisper/message.
            dispatch(setOnePassListValuesAction(true, [], 0));
            dispatch(setOnePassListOffsetAction(offset));
            dispatch(setOnePassIsListLoadedAction(true));
            Log.e(TAG, 'stacktrace', error);
        }
    };

export const resetOnePassListProperties = () => (dispatch: Function) => {
    dispatch(resetOnePassListAction(false, 0, 0, [], 0));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyBackPcPinOverlay());
        dispatch(setListEntryFocus(ListTypes.ONEPASS_MANAGER, true));
        return;
    }

    dispatch(resetOnePassListProperties());
    dispatch(showPreviousScreen());
};

export const onKeyGuideOnePassManager = () => (dispatch: Function) => {
    dispatch(resetOnePassListProperties());
    dispatch(showGuideScreen());
};

export const onKeyChannelDownOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const {
        onePassManager: { offset, isListLoaded, totalCount },
        screens: { screenTaskController },
    } = getState();

    if (
        screenTaskController !== ScreenTaskControllerTypes.ONEPASS_MANAGER_DEFAULT_TASKS ||
        !isListLoaded
    )
        return;

    const calibrationValue = offset === 0 ? OPM_LIST_CALIBRATION : OPM_LIST_CALIBRATION2;
    if (totalCount >= offset + calibrationValue) {
        dispatch(updateOnePassList(offset + calibrationValue - 2, 1));
    }
};

export const onKeyChannelUpOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const {
        onePassManager: { offset, isListLoaded },
        screens: { screenTaskController },
    } = getState();

    if (
        screenTaskController !== ScreenTaskControllerTypes.ONEPASS_MANAGER_DEFAULT_TASKS ||
        !isListLoaded
    )
        return;

    if (offset > 0) {
        const newOffset = offset < OPM_LIST_CALIBRATION ? 0 : offset - OPM_LIST_CALIBRATION2 + 2;

        dispatch(
            updateOnePassList(
                newOffset,
                newOffset === 0 ? OPM_LIST_CALIBRATION - 2 : OPM_LIST_CALIBRATION2 - 2,
            ),
        );
    }
};

export const onKeyDownOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const {
        list,
        onePassManager: { offset, totalCount, onePassList, isListLoaded },
        screens: { screenTaskController },
    } = getState();
    const position = list[ListTypes.ONEPASS_MANAGER]?.position;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyDownPcPinOverlay());
        return;
    }

    if (!onePassList?.length) return;
    if (!isListLoaded) return;

    if (
        offset === 0 &&
        position + 2 === OPM_LIST_CALIBRATION &&
        totalCount >= offset + OPM_LIST_CALIBRATION
    ) {
        dispatch(setOnePassIsListLoadedAction(false));
        dispatch(updateOnePassList(offset + OPM_LIST_CALIBRATION - 2, 1));
    } else if (
        position + 2 === OPM_LIST_CALIBRATION2 &&
        totalCount >= offset + OPM_LIST_CALIBRATION2
    ) {
        dispatch(setOnePassIsListLoadedAction(false));
        dispatch(updateOnePassList(offset + OPM_LIST_CALIBRATION2 - 2, 1));
    } else {
        dispatch(onKeyDownList(ListTypes.ONEPASS_MANAGER));
    }
};

export const transitionToCollectionScreen =
    (unifiedCollectionId: string, position: number, offset: number) => (dispatch: Function) => {
        if (!KeyManager.getInstance().isKeyIgnoring()) KeyManager.getInstance().startKeyIgnoring();

        const service = new ServicesClientImpl();

        const whisperMessage = BrandingUiUtility.formatDescription(
            Locale.getString(LocaleKeys.NO_NETWORK_WHISPER_DESCRIPTION),
            {
                DEVICE_NAME_SHORT: service.getBrandingUiInfoForKey(
                    BrandingUiInfoKeys.DEVICE_NAME_SHORT,
                ),
                BUTTON_NAME_SELECT: DeviceModel.isWebOsPlatform()
                    ? Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_LG)
                    : Locale.getString(LocaleKeys.BUTTON_NAME_SELECT_SAMSUNG),
            },
        );
        if (!NetworkManager.isNetworkDisconnected()) {
            dispatch(resetOnePassListAction(false, position, offset, [], 0));
            dispatch(showCollectionsScreen(unifiedCollectionId));
        } else {
            dispatch(showWhisper({ message: whisperMessage, iconType: WhisperTypes.NO_ICON }));
            KeyManager.getInstance().stopKeyIgnoring();
        }
    };

export const onKeyOkOnePassManager = () => async (dispatch: Function, getState: TGetState) => {
    const {
        list,
        onePassManager: { offset, onePassList },
        screens: { screenTaskController },
    } = getState();
    const position = list[ListTypes.ONEPASS_MANAGER]?.position;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyOkPcPinOverlay());
        return;
    }

    if (!onePassList?.length) return;

    const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();
    if (isNetworkDisconnected) {
        dispatch(
            showWhisper({
                message: NetworkManager.getNetworkDisconnectedMessage(),
                iconType: WhisperTypes.INFO,
            }),
        );
        return;
    }

    const unifiedCollectionId = onePassList[position]?.unifiedCollectionId;
    const service = new ServicesClientImpl();
    let preview: TUnifiedCollectionPreview;

    if (!unifiedCollectionId) {
        Log.e(TAG, 'Unified Collection Id is not available');
        return;
    }

    KeyManager.getInstance().startKeyIgnoring();

    // TODO : Need to update this later when preview is part of release.
    try {
        preview = await new NdvrInteractor().getPreviewForUCId({
            unifiedCollectionId,
        });
    } catch (error) {
        Log.e(TAG, 'stacktrace', error);
        KeyManager.getInstance().stopKeyIgnoring();
        return;
    }

    const { internalRatingList = [], unifiedCollectionType } = preview;
    const isRestricted = service.checkPCViolationsWithRatings(
        internalRatingList,
        !!onePassList[position].isAdult,
        unifiedCollectionType,
    );

    if (isRestricted) {
        const ratingName = service.getRestrictedRatingName(internalRatingList);

        dispatch(setListEntryFocus(ListTypes.ONEPASS_MANAGER, false));
        dispatch(
            showPCPinOverlayWithProgramRating(
                () => transitionToCollectionScreen(unifiedCollectionId, position, offset),
                !!onePassList[position].isAdult,
                ratingName,
            ),
        );
    } else dispatch(transitionToCollectionScreen(unifiedCollectionId, position, offset));
};

export const onKeyUpOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const {
        list,
        onePassManager: { offset, onePassList, isListLoaded },
        screens: { screenTaskController },
    } = getState();
    const position = list[ListTypes.ONEPASS_MANAGER]?.position;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyUpPcPinOverlay());
        return;
    }

    if (!onePassList?.length) return;
    if (!isListLoaded) return;

    if (offset !== 0 && position - 1 === 0) {
        const newOffset = offset < OPM_LIST_CALIBRATION ? 0 : offset - OPM_LIST_CALIBRATION2 + 2;

        dispatch(setOnePassIsListLoadedAction(false));
        dispatch(
            updateOnePassList(
                newOffset,
                newOffset === 0 ? OPM_LIST_CALIBRATION - 2 : OPM_LIST_CALIBRATION2 - 2,
            ),
        );
    } else {
        dispatch(onKeyUpList(ListTypes.ONEPASS_MANAGER));
    }
};

export const onKeyLeftOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyLeftPcPinOverlay());
};

export const onKeyRightOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyRightPcPinOverlay());
};

export const onKeyDigitOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyDigitPcPinOverlay());
};

export const onMouseClickOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState();

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onMouseClickPcPinOverlay());
        return;
    }

    switch (value) {
        case InteractiveComponents.NORTH:
            dispatch(onKeyChannelUpOnePassManager());
            break;
        case InteractiveComponents.SOUTH:
            dispatch(onKeyChannelDownOnePassManager());
            break;
        default:
            if (value.includes(ListTypes.ONEPASS_MANAGER)) dispatch(onKeyOkOnePassManager());
    }
};

export const onMouseOverOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const {
        inputFactory: { value },
        onePassManager: { offset, totalCount },
        screens: { screenTaskController },
    } = getState();

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onMouseOverPcPinOverlay());
        return;
    }

    switch (value) {
        case InteractiveComponents.NORTH:
            if (offset !== 0) {
                dispatch(setActiveInteractiveComponent(value));
                dispatch(setOnePassManagerIsCaretVisibleAction(true));
            }
            return;
        case InteractiveComponents.SOUTH:
            if (totalCount > offset + OPM_LIST_CALIBRATION) {
                dispatch(setActiveInteractiveComponent(value));
                dispatch(setOnePassManagerIsCaretVisibleAction(true));
            }
            return;
        default:
    }

    dispatch(onMouseOverList());
};

export const onMouseOutOnePassManager = () => (dispatch: Function, getState: TGetState) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState();

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onMouseOutPcPinOverlay());
        return;
    }

    switch (value) {
        case InteractiveComponents.NORTH:
        case InteractiveComponents.SOUTH:
            dispatch(clearActiveInteractiveComponent());
            dispatch(setOnePassManagerIsCaretVisibleAction(false));
            return;
        default:
    }

    dispatch(onMouseOutList());
};

export const onMouseWheelOnePassManager =
    (wheelDeltaY: number) => (dispatch: Function, getState: TGetState) => {
        const { screenTaskController } = getState().screens;

        if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
            return; // no action needed
        }

        if (wheelDeltaY < 0) dispatch(onKeyDownOnePassManager());
        if (wheelDeltaY > 0) dispatch(onKeyUpOnePassManager());
    };

export const onNetworkStateChangeOPMOperations =
    () => (dispatch: Function, getState: TGetState) => {
        const { onePassList, lastSelectedPosition, offset } = getState().onePassManager;

        if (onePassList.length === 0) {
            dispatch(updateOnePassList(offset, lastSelectedPosition));
        }
    };

export default onePassManager;
